import { type ComponentPropsWithoutRef, type ReactNode } from 'react';
import cx from 'clsx';
import { useIntl } from 'react-intl';
import { useSegment } from '@repo/poly-hooks/segment';
import { CopyToClipboardButton } from '@repo/shared-kit/components';
import { useToast } from '@repo/design-system-kit';
import styles from './copy-field.module.css';

interface CopyFieldProps extends ComponentPropsWithoutRef<'label'> {
  label: string;
  value: string;
  displayValue?: string;
  showCurrency?: boolean;
  currency?: string;
  trackEvent?: string;
}

export function CopyField({
  label,
  value,
  trackEvent,
  className,
  ...props
}: CopyFieldProps): ReactNode {
  const { formatMessage } = useIntl();
  const segment = useSegment();
  const { showToast } = useToast();

  const handleCopySuccess = (): void => {
    if (trackEvent) {
      segment.track(trackEvent);
    }
  };

  const handleCopyError = (): void => {
    showToast({
      type: 'info',
      text: formatMessage({ id: 'copy-clipboard.fail' }),
    });
  };

  return (
    <label className={cx(styles.label, className)} {...props}>
      <p className="mb-4 body-1" data-testid="copy-field-label">
        {label}
      </p>
      <CopyToClipboardButton
        className={cx('btn', styles.copyBtn)}
        clipboardText={value}
        data-test-copy-button={value}
        hasIcon
        mode="clipboard"
        onError={handleCopyError}
        onSuccess={handleCopySuccess}
        toastLabel={formatMessage({ id: 'copy-clipboard.success' })}
      >
        {value}
      </CopyToClipboardButton>
    </label>
  );
}
